import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoPicker } from "../equipment/shared/PhotoPicker";
import { HoManualEntryFields, todayLocal } from "./shared/HoManualEntryFields";

const DIESEL_KINDS = [
  { key: "received", label: "Diesel Received" },
  { key: "issued", label: "Diesel Issued" },
];

export default function DieselItemForm({ kind: kindProp, dayId, site: siteProp, initial, onClose, onSaved, manual = false, sites = [] }) {
  const isEdit = !!initial;
  const [kind, setKind] = useState(kindProp);
  const [manualSite, setManualSite] = useState("");
  const [entryDate, setEntryDate] = useState(todayLocal());
  const site = manual ? manualSite : siteProp;
  const [quantity, setQuantity] = useState(initial?.quantity ?? "");
  const [currentReading, setCurrentReading] = useState(initial?.current_reading ?? "");
  const [remarks, setRemarks] = useState(initial?.remarks ?? "");
  const [photos, setPhotos] = useState([]);
  const [equipmentType, setEquipmentType] = useState("site_asset");
  const [equipmentRef, setEquipmentRef] = useState("");
  const [options, setOptions] = useState({ site_assets: [], hired_equipment: [] });
  const [showNewHired, setShowNewHired] = useState(false);
  const [newHiredName, setNewHiredName] = useState("");
  const [newHiredContractor, setNewHiredContractor] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (kind !== "issued" || isEdit) return;
    if (manual && !manualSite) return;
    setEquipmentRef("");
    client
      .get("/diesel-register/equipment-options", { params: site ? { site } : {} })
      .then((res) => setOptions(res.data))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, [kind, isEdit, site, manual, manualSite]);

  const equipList = equipmentType === "site_asset" ? options.site_assets : options.hired_equipment;

  const submit = async (e) => {
    e.preventDefault();
    if (manual && !manualSite) return toast.error("Select a site");
    if (manual && !entryDate) return toast.error("Entry Date is required");
    if (manual && entryDate > todayLocal()) return toast.error("Entry Date cannot be in the future");
    const qty = parseFloat(quantity);
    if (!(qty > 0)) return toast.error("Quantity must be greater than 0");
    if (!isEdit && photos.length === 0) return toast.error("At least one photo is required");
    setBusy(true);
    try {
      const fd = new FormData();
      if (kind === "issued" && !isEdit) {
        let ref = equipmentRef;
        let type = equipmentType;
        if (showNewHired) {
          if (!newHiredName.trim()) {
            toast.error("Equipment Name/Description is required");
            setBusy(false);
            return;
          }
          const res = await client.post("/diesel-register/hired-equipment", {
            equipment_name: newHiredName.trim(),
            contractor_owner: newHiredContractor.trim() || null,
          });
          ref = res.data.code;
          type = "hired";
        } else if (!ref) {
          toast.error("Select equipment");
          setBusy(false);
          return;
        }
        const reading = parseFloat(currentReading);
        if (!(reading >= 0)) {
          toast.error("Current Reading must be 0 or more");
          setBusy(false);
          return;
        }
        fd.append("equipment_type", type);
        fd.append("equipment_ref", ref);
        fd.append("current_reading", reading);
      }
      if (kind === "issued" && isEdit && currentReading !== "") fd.append("current_reading", parseFloat(currentReading));
      fd.append("quantity", qty);
      if (manual) {
        fd.append("site", manualSite);
        fd.append("entry_date", entryDate);
      }
      if (remarks.trim()) fd.append("remarks", remarks.trim());
      photos.forEach((p) => fd.append("photos", p));

      const url = isEdit
        ? `/diesel-register/day/${dayId}/${kind}/${initial.item_id}`
        : manual
          ? `/diesel-register/manual/${kind}`
          : dayId
          ? `/diesel-register/day/${dayId}/${kind}`
          : `/diesel-register/${kind}`;
      const res = isEdit ? await client.put(url, fd) : await client.post(url, fd);
      if (res.data.negative_balance_warning) {
        toast.warning("Diesel balance has gone negative for this site. Please verify the entries.");
      }
      toast.success(isEdit ? "Entry updated" : "Entry added");
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="diesel-item-form">
        <DialogHeader>
          <DialogTitle>
            {manual ? "Add entry (HO)" : `${isEdit ? "Edit" : "Add"} ${kind === "received" ? "Diesel Received" : "Diesel Issued"}`}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {manual && (
            <HoManualEntryFields prefix="diesel" sites={sites} site={manualSite} onSite={setManualSite} date={entryDate} onDate={setEntryDate} kinds={DIESEL_KINDS} kind={kind} onKind={setKind} />
          )}
          {kind === "issued" && !isEdit && (
            <>
              <div>
                <p className="text-sm font-medium text-slate-700 mb-1.5">Equipment Type *</p>
                <Select value={equipmentType} onValueChange={setEquipmentType}>
                  <SelectTrigger data-testid="diesel-equipment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="site_asset" data-testid="diesel-equipment-type-site-asset">Site Asset</SelectItem>
                    <SelectItem value="hired" data-testid="diesel-equipment-type-hired">Hired Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!showNewHired ? (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1.5">Equipment *</p>
                  <SearchableSelect testId="diesel-equipment-select" value={equipmentRef} onValueChange={setEquipmentRef} placeholder="Select equipment"
  options={equipList.map((eq) => ({ value: eq.id, label: `${eq.label}${eq.contractor_owner ? ` — ${eq.contractor_owner}` : ""}`, testId: `diesel-equipment-option-${eq.id}` }))} />
                  {equipmentType === "hired" && (
                    <button
                      type="button"
                      data-testid="diesel-new-hired-toggle"
                      onClick={() => setShowNewHired(true)}
                      className="mt-1.5 text-xs font-medium text-amber-700 hover:underline"
                    >
                      + Add new Hired Equipment
                    </button>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-amber-800">New Hired Equipment</p>
                  <Input
                    data-testid="diesel-new-hired-name"
                    placeholder="Equipment Name/Description *"
                    value={newHiredName}
                    onChange={(e) => setNewHiredName(e.target.value)}
                  />
                  <Input
                    data-testid="diesel-new-hired-contractor"
                    placeholder="Contractor/Owner (optional)"
                    value={newHiredContractor}
                    onChange={(e) => setNewHiredContractor(e.target.value)}
                  />
                  <button
                    type="button"
                    data-testid="diesel-new-hired-cancel"
                    onClick={() => setShowNewHired(false)}
                    className="text-xs text-slate-500 hover:underline"
                  >
                    Cancel — pick existing equipment
                  </button>
                </div>
              )}
            </>
          )}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Quantity (Litres) *</p>
            <Input
              type="number"
              step="any"
              data-testid="diesel-quantity-input"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          {kind === "issued" && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Current Reading (HMR/KM) {isEdit ? "" : "*"}</p>
              <Input
                type="number"
                step="any"
                data-testid="diesel-reading-input"
                value={currentReading}
                onChange={(e) => setCurrentReading(e.target.value)}
              />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Remarks</p>
            <Input data-testid="diesel-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          <PhotoPicker
            label={isEdit ? "Add Photos (optional)" : "Photos"}
            files={photos}
            onChange={setPhotos}
            testId="diesel-photos-input"
            required={!isEdit}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="diesel-item-submit" className="bg-amber-600 hover:bg-amber-700">
              {busy ? "Saving..." : isEdit ? "Save Changes" : "Add Entry"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
